import type { CompanySettings, PacaLetter, PacaSettings } from '@morales/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api, newIdempotencyKey } from '../api';
import { useAuth } from '../auth';
import { DecoRule, OakMark, Wordmark } from '../components/brand';
import { Empty, ErrorBox, PageHead, QueryView } from '../components/ui';
import { formatDate, formatMoney } from '../format';
import { useLocale } from '../i18n';
import { qk } from '../queryClient';
import { PACA_NOTICE, pacaClause } from '../components/InvoicePaper';

export { pacaClause };

/** PACA settings (owner): the license on the invoice, the interest and the attorney-fee clause. */
export function PacaSettingsPage() {
  const { t } = useLocale();
  const { me } = useAuth();
  const qc = useQueryClient();
  const settings = useQuery({ queryKey: qk.pacaSettings, queryFn: ({ signal }) => api.pacaSettings(signal) });
  const [form, setForm] = useState<{ licenseNo: string; interestPct: string; attorneyFees: boolean } | null>(null);
  const save = useMutation({
    mutationFn: (body: PacaSettings) => api.setPacaSettings(body),
    onSuccess: (s) => {
      qc.setQueryData(qk.pacaSettings, s);
      setForm(null);
    },
  });
  const owner = me?.role === 'owner';
  return (
    <div className="stack">
      <PageHead title={t('pacaTitle')} back="/" />
      <p className="muted">{t('pacaHint')}</p>
      <p className="note">{t('pacaOffUntilLicense')}</p>
      <QueryView query={settings}>
        {(s) => {
          const f = form ?? { licenseNo: s.licenseNo ?? '', interestPct: String(s.interestPct), attorneyFees: s.attorneyFees };
          return (
            <section className="card stack-sm">
              <label className="field">
                <span>{t('pacaLicense')}</span>
                <input type="text" autoComplete="off" value={f.licenseNo} onChange={(e) => setForm({ ...f, licenseNo: e.target.value })} readOnly={!owner} aria-label={t('pacaLicense')} />
              </label>
              <label className="field">
                <span>{t('pacaInterest')}</span>
                <input type="text" inputMode="decimal" autoComplete="off" value={f.interestPct} onChange={(e) => setForm({ ...f, interestPct: e.target.value })} readOnly={!owner} aria-label={t('pacaInterest')} style={{ width: 90 }} />
              </label>
              <label className="check">
                <input type="checkbox" checked={f.attorneyFees} disabled={!owner} onChange={(e) => setForm({ ...f, attorneyFees: e.target.checked })} /> {t('pacaFees')}
              </label>
              <p className="muted">{t('pacaClausePreview')}: {pacaClause({ licenseNo: f.licenseNo || null, interestPct: Number(f.interestPct) || 0, attorneyFees: f.attorneyFees }) || '—'}</p>
              {owner && form ? (
                <button type="button" className="btn btn-primary btn-block" disabled={save.isPending || !Number.isFinite(Number(f.interestPct))} onClick={() => save.mutate({ licenseNo: f.licenseNo.trim() || null, interestPct: Number(f.interestPct), attorneyFees: f.attorneyFees })}>
                  {t('save')}
                </button>
              ) : null}
              {save.error ? <ErrorBox error={save.error} /> : null}
              <p className="muted">{t('pacaTermsRule')}</p>
            </section>
          );
        }}
      </QueryView>
      <LetterheadCard />
    </div>
  );
}

const LETTERHEAD_FIELDS = [
  ['name', 'companyNameLabel'],
  ['addressLine1', 'addressLine1Label'],
  ['addressLine2', 'addressLine2Label'],
  ['city', 'city'],
  ['state', 'stateLabel'],
  ['zip', 'zip'],
  ['phone', 'phone'],
  ['email', 'email'],
] as const;

/** The letterhead printed at the top of every invoice: the owner types it once here. */
function LetterheadCard() {
  const { t } = useLocale();
  const { me } = useAuth();
  const qc = useQueryClient();
  const company = useQuery({ queryKey: qk.companySettings, queryFn: ({ signal }) => api.companySettings(signal) });
  const [form, setForm] = useState<CompanySettings | null>(null);
  const save = useMutation({
    mutationFn: (body: CompanySettings) => api.setCompanySettings(body),
    onSuccess: (c) => {
      qc.setQueryData(qk.companySettings, c);
      setForm(null);
    },
  });
  const owner = me?.role === 'owner';
  return (
    <section className="card stack-sm" aria-labelledby="letterhead-title">
      <h2 id="letterhead-title">{t('letterheadTitle')}</h2>
      <p className="muted">{t('letterheadHint')}</p>
      <QueryView query={company}>
        {(c) => {
          const f = form ?? c;
          return (
            <>
              <div className="form-grid">
                {LETTERHEAD_FIELDS.map(([key, label]) => (
                  <label key={key} className="field">
                    <span>{t(label)}</span>
                    <input type={key === 'email' ? 'email' : key === 'phone' ? 'tel' : 'text'} autoComplete="off" value={f[key]} readOnly={!owner} onChange={(e) => setForm({ ...f, [key]: e.target.value })} />
                  </label>
                ))}
              </div>
              {owner && form ? (
                <button type="button" className="btn btn-primary" disabled={save.isPending || !f.name.trim()} onClick={() => save.mutate(f)}>
                  {t('saveLetterhead')}
                </button>
              ) : null}
              {save.isSuccess && !form ? (
                <p className="note" role="status">
                  {t('letterheadSaved')}
                </p>
              ) : null}
              {save.error ? <ErrorBox error={save.error} /> : null}
            </>
          );
        }}
      </QueryView>
    </section>
  );
}

/** The demand letter: every open invoice with interest to date, the trust claim, print it, record it as sent. */
export function PacaLetterPage() {
  const { t, intlLocale } = useLocale();
  const { id = '' } = useParams();
  const qc = useQueryClient();
  const query = useQuery({ queryKey: qk.pacaLetter(id), queryFn: ({ signal }) => api.pacaLetter(id, signal) });
  const [key, setKey] = useState(() => newIdempotencyKey());
  const record = useMutation({
    mutationFn: (body: string) => api.recordPacaNotice(id, { kind: 'demand', body }, key),
    onSuccess: () => {
      setKey(newIdempotencyKey());
      void qc.invalidateQueries({ queryKey: qk.pacaLetter(id) });
      void qc.invalidateQueries({ queryKey: qk.aging });
    },
  });
  // The record is the letter as printed: the same strings, in the language it was printed in.
  const letterText = (l: PacaLetter) =>
    [
      `${formatDate(l.asOf, intlLocale, 'date')} · ${l.licenseNo ? `PACA ${t('pacaLicenseShort')} ${l.licenseNo}` : ''}`,
      `${t('demandTo')}: ${l.customerName}${l.customerAddress ? `, ${l.customerAddress}` : ''} · ${t('terms')}: ${l.termsDays} ${t('days')}`,
      t('demandBody1').replace('{payBy}', formatDate(l.payBy, intlLocale, 'date')),
      ...l.invoices.map((i) => `${t('invoice')} ${i.invoiceNo} · ${formatDate(i.date, intlLocale, 'date')} · ${t('dueDate')} ${i.dueDate ? formatDate(i.dueDate, intlLocale, 'date') : '—'} · ${i.daysLate} ${t('daysLate')} · ${formatMoney(i.balance, intlLocale)} · ${t('interest')} ${formatMoney(i.interest, intlLocale)}`),
      `${t('principal')} ${formatMoney(l.principal, intlLocale)} · ${t('interest')} ${formatMoney(l.interest, intlLocale)} · ${t('total')} ${formatMoney(l.total, intlLocale)}`,
      PACA_NOTICE,
      pacaClause(l),
      t('demandBody2'),
    ].join('\n');
  return (
    <QueryView query={query}>
      {(l) => (
        <div className="stack invoice paca-letter">
          <PageHead title={`${t('pacaLetter')}: ${l.customerName}`} back={`/customers/${l.customerId}/statement`} />
          <header className="letterhead print-only">
            <OakMark />
            <Wordmark />
            <DecoRule />
            <div className="letterhead-row">
              <span>{l.licenseNo ? `PACA ${t('pacaLicenseShort')} ${l.licenseNo}` : ''}</span>
              <span>{formatDate(l.asOf, intlLocale, 'date')}</span>
            </div>
          </header>
          <section className="card stack-sm">
            <h2>{t('demandTo')}</h2>
            <span className="row-title big">{l.customerName}</span>
            {l.customerAddress ? <span className="muted">{l.customerAddress}</span> : null}
            <span className="muted">
              {t('terms')}: {l.termsDays} {t('days')}
              {l.termsAgreedOn ? ` · ${t('termsAgreedOn')} ${formatDate(l.termsAgreedOn, intlLocale, 'date')}` : ''}
            </span>
            <p>{t('demandBody1').replace('{payBy}', formatDate(l.payBy, intlLocale, 'date'))}</p>
          </section>
          {l.invoices.length === 0 ? (
            <Empty>{t('nothingOpen')}</Empty>
          ) : (
            <ul className="list" data-testid="paca-invoices">
              {l.invoices.map((i) => (
                <li key={i.id} className="row">
                  <span className="row-main">
                    <span className="row-title">
                      {t('invoice')} {i.invoiceNo}
                    </span>
                    <span className="row-sub">
                      {formatDate(i.date, intlLocale, 'date')} · {t('dueDate')} {i.dueDate ? formatDate(i.dueDate, intlLocale, 'date') : '—'} · {i.daysLate} {t('daysLate')}
                      {/* for the office, never on the printed letter the customer gets */}
                      {i.noticeDaysLeft > 0 && i.daysLate > 0 ? <span className="no-print">{` · ${t('noticeDaysLeft').replace('{n}', String(i.noticeDaysLeft))}`}</span> : null}
                    </span>
                  </span>
                  <span className="row-end">
                    <span className="row-end-value owed">{formatMoney(i.balance, intlLocale)}</span>
                    {i.interest ? <span className="row-end-label">+ {formatMoney(i.interest, intlLocale)} {t('interest')}</span> : null}
                  </span>
                </li>
              ))}
            </ul>
          )}
          <dl className="facts totals">
            <div className="fact">
              <dt>{t('principal')}</dt>
              <dd>{formatMoney(l.principal, intlLocale)}</dd>
            </div>
            <div className="fact">
              <dt>
                {t('interest')} ({l.interestPct}%)
              </dt>
              <dd>{formatMoney(l.interest, intlLocale)}</dd>
            </div>
            <div className="fact">
              <dt>{t('total')}</dt>
              <dd className="money">{formatMoney(l.total, intlLocale)}</dd>
            </div>
          </dl>
          <p className="paca">{PACA_NOTICE}</p>
          <p className="paca">{pacaClause(l)}</p>
          <p className="note print-only">{t('demandBody2')}</p>
          {l.notices.length > 0 ? (
            <section className="card stack-sm no-print">
              <h2>{t('lettersSent')}</h2>
              <ul className="list">
                {l.notices.map((n) => (
                  <li key={n.id} className="row">
                    <span className="row-main">
                      <span className="row-title">{formatDate(n.sentOn, intlLocale, 'date')}</span>
                      <span className="row-sub">
                        {n.invoiceCount} · {n.byName ?? ''}
                      </span>
                    </span>
                    <span className="row-end-value">{formatMoney(n.principal + n.interest, intlLocale)}</span>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
          {record.error ? <ErrorBox error={record.error} /> : null}
          {record.isSuccess ? <p className="note no-print" role="status">✓ {t('letterRecorded')}</p> : null}
          {l.invoices.length > 0 ? (
            <div className="stack-sm no-print">
              <button type="button" className="btn btn-primary btn-lg btn-block" onClick={() => window.print()}>
                {t('print')}
              </button>
              <button type="button" className="btn btn-secondary btn-lg btn-block" disabled={record.isPending || record.isSuccess} onClick={() => record.mutate(letterText(l))}>
                {t('recordLetterSent')}
              </button>
              <Link to={`/customers/${l.customerId}/statement`} className="btn btn-ghost btn-block">
                {t('statement')}
              </Link>
            </div>
          ) : null}
        </div>
      )}
    </QueryView>
  );
}
